// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FeedbackIcon from "@mui/icons-material/Feedback";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommentIcon from "@mui/icons-material/Comment";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  fetchData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { manager360SummaryUrl } from "../../../Service/Apis";
import { formatDate } from "../utils";

interface AverageRatings {
  communication: number;
  support: number;
  recognition: number;
  development: number;
  leadership: number;
  overall: number;
}

interface RecentFeedback {
  submitted_date: string;
  overall_rating: number;
  period: string;
  is_anonymous: boolean;
  comments?: string | null;
}

interface FeedbackComment {
  submitted_date: string;
  overall_rating: number;
  period: string;
  comments: string;
}

interface Manager360Summary {
  total_feedback_count: number;
  average_ratings: AverageRatings;
  recent_feedback: RecentFeedback[];
  all_comments?: FeedbackComment[];  // New field
  message?: string;
}

// Rating category configuration
const RATING_CATEGORIES = [
  {
    key: 'communication' as const,
    title: 'Communication & Transparency',
    description: 'How well you communicate goals, expectations, and feedback'
  },
  {
    key: 'support' as const,
    title: 'Support & Guidance',
    description: 'Support provided when team members face challenges'
  },
  {
    key: 'recognition' as const,
    title: 'Recognition & Appreciation',
    description: 'Recognition and appreciation of contributions'
  },
  {
    key: 'development' as const,
    title: 'Career Development & Growth',
    description: 'Support for career growth and skill development'
  },
  {
    key: 'leadership' as const,
    title: 'Leadership & Decision Making',
    description: 'Effectiveness in leading the team and making decisions'
  }
];

const Manager360FeedbackView: React.FC = () => {
  // useState hooks
  const [summary, setSummary] = useState<Manager360Summary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Other hooks
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> = useDispatch();
  const showToast = useCustomToast();

  // Utility Functions
  const getRatingColor = (rating: number): string => {
    if (rating >= 4.5) return "#2e7d32"; // Excellent - Dark Green
    if (rating >= 4.0) return "#388e3c"; // Very Good - Green
    if (rating >= 3.5) return "#689f38"; // Good - Light Green
    if (rating >= 3.0) return "#fbc02d"; // Average - Yellow
    if (rating >= 2.5) return "#f57c00"; // Below Average - Orange
    return "#d32f2f"; // Needs Improvement - Red
  };

  const getRatingLabel = (rating: number): string => {
    if (rating >= 4.5) return "Excellent";
    if (rating >= 4.0) return "Very Good";
    if (rating >= 3.5) return "Good";
    if (rating >= 3.0) return "Average";
    if (rating >= 2.5) return "Below Average";
    return "Needs Improvement";
  };

  const getRatingPercentage = (rating: number): number => {
    return (rating / 5) * 100;
  };

  // Functions
  const fetchFeedbackSummary = async () => {
    setLoading(true);
    
    const defaultEmptyState: Manager360Summary = {
      total_feedback_count: 0,
      average_ratings: {
        communication: 0,
        support: 0,
        recognition: 0,
        development: 0,
        leadership: 0,
        overall: 0
      },
      recent_feedback: [],
      all_comments: [],
      message: "No feedback data available"
    };

    try {
      const response = await dispatch(fetchData(manager360SummaryUrl)) as any;

      if (!response || response === null || response === undefined) {
        setSummary({
          ...defaultEmptyState,
          message: "No response received from server. Please check your connection."
        });
        return;
      }

      if (typeof response === 'object') {
        if ('total_feedback_count' in response) {
          const summaryData: Manager360Summary = {
            total_feedback_count: response.total_feedback_count || 0,
            average_ratings: response.average_ratings || {
              communication: 0,
              support: 0,
              recognition: 0,
              development: 0,
              leadership: 0,
              overall: 0
            },
            recent_feedback: response.recent_feedback || [],
            all_comments: response.all_comments || [],
            message: response.message
          };
          setSummary(summaryData);
        } else {
          setSummary({
            ...defaultEmptyState,
            message: "Unexpected response format from server"
          });
        }
      } else {
        setSummary({
          ...defaultEmptyState,
          message: "Invalid response format"
        });
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      setSummary({
        ...defaultEmptyState,
        message: axiosError.response?.data?.message || axiosError.message || "Failed to load 360° feedback. Please try again later."
      });
      
      if (axiosError.response && 'status' in axiosError.response && axiosError.response.status !== 401) {
        showToast(
          axiosError.response?.data?.message || axiosError.message || "Failed to load 360° feedback", 
          "error", 
          true
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchFeedbackSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter comments that actually have text
  const commentsWithText = summary?.all_comments?.filter(
    (comment) => comment.comments && comment.comments.trim().length > 0
  ) || [];

  // Return JSX
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // No feedback case
  if (!summary || summary.total_feedback_count === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Box sx={{ mb: 3 }}>
          <PageTitle>360° Feedback</PageTitle>
        </Box>

        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <FeedbackIcon
            sx={{ fontSize: 80, color: "black", mb: 2 }}
          />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Feedback Received Yet
          </Typography>
          <Typography variant="body2" color="black">
            {summary?.message || "You haven't received any 360° feedback from your team members yet. Feedback will appear here once your team completes their reviews."}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <PageTitle>360° Feedback</PageTitle>
      </Box>

      {/* Overall Rating Card */}
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <StarIcon sx={{ fontSize: 32, color: "primary.main", mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Overall Rating
            </Typography>
          </Box>
          
          <Box>
            <Box sx={{ textAlign: { xs: "center", md: "left" }, mb: 2 }}>
              <Typography variant="h2" sx={{ fontWeight: "bold", color: getRatingColor(summary.average_ratings.overall), mb: 1 }}>
                {summary.average_ratings.overall.toFixed(2)}
                <Typography component="span" variant="h5" sx={{ ml: 1, color: "black" }}>
                  / 5.00
                </Typography>
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, color: "black" }}>
                {getRatingPercentage(summary.average_ratings.overall).toFixed(1)}%
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="black" sx={{ mb: 1 }}>
                Your team's overall perception of your leadership effectiveness
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getRatingPercentage(summary.average_ratings.overall)}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: getRatingColor(summary.average_ratings.overall),
                  },
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Category Ratings */}
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
        Leadership Competencies
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {RATING_CATEGORIES.map((category) => {
          const rating = summary.average_ratings[category.key];
          const percentage = getRatingPercentage(rating);
          const color = getRatingColor(rating);

          return (
            <Grid item xs={12} md={6} key={category.key}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "600", mb: 1 }}>
                    {category.title}
                  </Typography>
                  <Typography variant="caption" color="black" sx={{ mb: 2, display: "block" }}>
                    {category.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color }}>
                      {rating.toFixed(2)}
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: color,
                      },
                    }}
                  />
                  <Typography variant="caption" color="black" sx={{ mt: 1, display: "block" }}>
                    {percentage.toFixed(1)}% of maximum score
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Anonymous Comments Section */}
      {commentsWithText.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <CommentIcon sx={{ fontSize: 28, color: "primary.main", mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Anonymous Feedback Comments
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {commentsWithText.map((feedback, index) => (
              <Grid item xs={12} key={index}>
                <Card elevation={1} sx={{ bgcolor: "#f9f9f9" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", mb: 2 }}>
                      <Chip
                        label="Anonymous"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      "{feedback.comments}"
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* No Comments Message */}
      {commentsWithText.length === 0 && summary.total_feedback_count > 0 && (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center", bgcolor: "#f9f9f9" }}>
          <CommentIcon sx={{ fontSize: 48, color: "black", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Written Comments Yet
          </Typography>
          <Typography variant="body2" color="black">
            Your team members have provided ratings but haven't included written comments yet.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default Manager360FeedbackView;
